import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/it';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import 'dayjs/locale/sv';
import 'dayjs/locale/ru';
import { useState } from 'modules/auth/store/state'


dayjs.extend(localizedFormat);

type Unit = 'celsius' | 'voltage' | 'hertz' | string;

interface ConvertedValue {
  value: number;
  unit: string;
}

export const convertUnitIfNeeded = (value: number, unit: Unit): ConvertedValue => {
  const authState = useState()  
  const optionalSetting = ref(authState.setting || {});
  const locale = optionalSetting.value?.setting?.locale ?? 'en';

  if (locale === 'en') {
    if (unit === 'celsius') {
      return {
        value: parseFloat((value * 9 / 5 + 32).toFixed(2)),
        unit: 'fahrenheit'
      };
    }
  }

  if (locale !== 'en') {
    if (unit === 'psi' || unit === 'count') { // Assuming 'count' is also in psi for Inflate_Pressure_Max_Value
      return {
        value: value * 0.06895,
        unit: 'bar'
      };
    }
  }

  return { value, unit };
};

const formatsByLocale: Record<string, string> = {
  it: 'DD/MM/YYYY HH:mm',
  es: 'DD/MM/YYYY HH:mm',
  pt: 'DD/MM/YYYY HH:mm',
  fr: 'DD/MM/YYYY HH:mm',
  de: 'DD.MM.YYYY HH:mm',
  sv: 'YYYY-MM-DD HH:mm',
  ru: 'DD.MM.YYYY HH:mm',
  en: 'MM/DD/YYYY hh:mm A',
  //'en-us': 'MM/DD/YYYY hh:mm A',
  //'en-gb': 'DD/MM/YYYY HH:mm',
};

const getDefaultFormat = () => {
  const authState = useState()  
  const optionalSetting = ref(authState.setting || {});
  const locale = optionalSetting.value?.setting?.locale ?? 'en';  
  dayjs.locale(locale)

  return (
    formatsByLocale[locale] ||
    formatsByLocale[locale.split('-')[0]] ||
    'L LT'
  );
};

export const formatUpperCase = (
  value: string | null | undefined
): string => {
  return value ? value.toUpperCase() : "";
};

export const formatDateTime = (
  date?: Date | string | null,
  format?: string
) => {
  if (!date) return '';

  const d = dayjs(date);
  if (!d.isValid() || d.year() < 1980) return 'N/A';

  return d.format(format || getDefaultFormat());
};


export function unit(
  value: number,
  singularKey: string,
  pluralKey: string,
  t: (key: string) => string
): string {
  return `${value} ${t(value === 1 ? singularKey : pluralKey)}`;
}

export function inverterTicksToText(
  ticks: number,
  t: (key: string) => string
): string {
  const calculatedMilli = Math.floor(ticks * 846.15);

  let seconds = Math.floor(calculatedMilli / 1000);
  let minutes = Math.floor(seconds / 60);
  let hours   = Math.floor(minutes / 60);
  let days    = Math.floor(hours / 24);

  seconds %= 60;
  minutes %= 60;
  hours   %= 24;

  const years  = Math.floor(days / 365);
  days %= 365;

  const months = Math.floor(days / 30);
  days %= 30;

  const parts: string[] = [];

  if (years)   parts.push(unit(years,   'year',   'years',   t));
  if (months)  parts.push(unit(months,  'month',  'months',  t));
  if (days)    parts.push(unit(days,    'day',    'days',    t));
  if (hours)   parts.push(unit(hours,   'hour',   'hours',   t));
  if (minutes) parts.push(unit(minutes, 'minute', 'minutes', t));
  if (seconds) parts.push(unit(seconds, 'second', 'seconds', t));

  return parts.join(' ');
}

export const checkIsMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

import EN_FLAG from '@/assets/flags/en.png';
import DE_FLAG from '@/assets/flags/de.png';
import ES_FLAG from '@/assets/flags/es.png';
import FR_FLAG from '@/assets/flags/fr.png';
import IT_FLAG from '@/assets/flags/it.png';
import JP_FLAG from '@/assets/flags/jp.png';
import PT_FLAG from '@/assets/flags/pt.png';
import RU_FLAG from '@/assets/flags/ru.png';
import SV_FLAG from '@/assets/flags/sv.png';

const flags: Record<string, string> = {
  en: EN_FLAG,
  de: DE_FLAG,
  es: ES_FLAG,
  fr: FR_FLAG,
  it: IT_FLAG,
  //jp: JP_FLAG,
  pt: PT_FLAG,
  ru: RU_FLAG,
  sv: SV_FLAG,
};

export function getFlagIcon(lang: string): string | undefined {
  return flags[lang];
}


// Download
import { ElMessage } from 'element-plus';
import api from '@/api/utils'

type Translator = (key: string) => string

interface DownloadOptions {
  url: string
  filename: string
  t: Translator
}

export const downloadFile = async (
  event: Event,
  { url, filename, t }: DownloadOptions
): Promise<void> => {
  try {
    const response = await api.get(url, { responseType: 'blob' });
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(t('Failed to download the file'));
    }
    const blob = new Blob([response.data], { type: response.headers['content-type'] });
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(objectUrl);
    document.body.removeChild(a);

    ElMessage({
      message: t('File downloaded successfully!'),
      type: 'success',
    })
  } catch (error) {
    ElMessage({
      message: t('Error while downloading the file. Please try again later.'),
      type: 'error',
    })
  }
}


// Api Request
interface ApiResponse {
    status: number;
    data: any;
}

type RequestFunction = () => Promise<ApiResponse>;

export async function requestHandler(
  url: string,
  data: Record<string, any>,
  method: 'post' | 'put' | 'patch',
  t: (key: string) => string,
  successMessage: string,
  errorMessage: string,
  contentType: string = 'application/x-www-form-urlencoded'
): Promise<ApiResponse | void> {
  try {
    let requestData: any = data;
    if (contentType === 'application/x-www-form-urlencoded') {
      requestData = new URLSearchParams(data).toString();
    }
    //const dataEncoded = new URLSearchParams(data).toString();
    const requestFunction: RequestFunction = async () => {
      return await api[method](url, requestData, {
        headers: {
          'Content-Type': contentType,
        },
      });
    };

    const response = await requestFunction();
    console.log('API Response:', response);
    if (response.status === 200 || response.status === 201) {
      ElMessage({
        message: successMessage,
        type: 'success',
      });

      return response;
    } else {
      const serverMessage = response.data?.detail;
      console.log('ciao', response.data)
      ElMessage({
        message: serverMessage ? t(serverMessage) : errorMessage,
        type: 'error',
      });
    }
  } catch (error: any) {
    const serverMessage = error.response?.data?.detail;
    console.log('ciao2', error.response)
    ElMessage({
      message: serverMessage ? t(serverMessage) : errorMessage,
      type: 'error',
    });
    
    throw error;
  }
}


export const getApiTypes = (type: string, t: (key: string) => string) => {
  let apiUrlSuffix = '';
  let mapStateKey = '';
  let typeKey = 0;
  let titleType = '';

  if (type === 'wheel') {
    apiUrlSuffix = 'wb';
    mapStateKey = 'mapWb';
    typeKey = 2;
    titleType = t('Wheel Balancers');
  } else if (type === 'tyre') {
    apiUrlSuffix = 'tc';
    mapStateKey = 'mapTc';
    typeKey = 3;
    titleType = t('Tire Changers');
  } else {
    apiUrlSuffix = '';
    mapStateKey = 'map';
    typeKey = 1;
    titleType = t('Devices');
  }

  return { apiUrlSuffix, mapStateKey, typeKey, titleType };
};

import { ref, Ref } from 'vue'

export interface DateFilters {
  gte: string
  lte: string
}

export interface DatetimeEntry {
  dateFilters?: DateFilters
  [key: string]: any
}

export interface DatetimeState {
  datetime?: Record<string, DatetimeEntry>
}

export function useDatetimeRange(idDatetime: string): {
  dateRange: Ref<[Date, Date]>
} {
  const authState = useState()
  const datetimeState = ref<DatetimeState>(authState.datetime || {})
  const dateRange = ref<[Date, Date]>([new Date(), new Date()])

  const loadDatetime = (): void => {
    const stored = datetimeState.value?.datetime?.[idDatetime]?.dateFilters

    if (stored?.gte && stored?.lte) {
      dateRange.value = [
        dayjs(stored.gte).toDate(),
        dayjs(stored.lte).toDate()
      ]
    } else { 
      const start = dayjs().subtract(1, 'month').startOf('day').toDate()
      const end = dayjs().endOf('day').toDate()
      dateRange.value = [start, end]
    }
  }

  loadDatetime()

  return {
    dateRange
  }
}

// Permissions
const rolesDefault: number[] = [1, 5];
const rolesMaintenance: number[] = [1, 3, 5];
const rolesBackOffice: number[] = [5, 11];
const rolesDealer: number[] = [5, 14, 7];

type PermissionsMap = {
  [routeName: string]: {
    [functionName: string]: number[];
  };
};

const permissionsMap: PermissionsMap = {
  "Devices": {
    "add_fn_1": rolesDefault,
    "flt_fn_1": rolesDefault,
    "btt_fn_1": rolesDefault
  },
  "Wheel Balancer": {
    "add_fn_1": rolesDefault,
    "flt_fn_1": rolesDefault,
    "btt_fn_1": rolesDefault
  },
  "Tire Changer": {
    "add_fn_1": rolesDefault,
    "flt_fn_1": rolesDefault,
    "btt_fn_1": rolesDefault
  },
  "Product Wheel Balancer": {
    "add_fn_1": rolesDefault,
    "flt_fn_1": rolesDefault,
  },
  "Product Tire Changer": {
    "add_fn_1": rolesDefault,
    "flt_fn_1": rolesDefault,
  },
  "Field Reports": {
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault
  },
  "Users": {
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault
  },
  "Groups": {
    "add_fn_1": rolesDefault,
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault
  },
  "Pending Error Rules": {
    "add_fn_1": rolesDefault,
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault
  },
  "Group App Permissions": {
    "upd_fn_1": rolesDefault
  },
  "Categories": {
    "add_fn_1": rolesDefault,
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault
  },
  "Subcategories": {
    "add_fn_1": rolesDefault,
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault
  },
  "Brand": {
    "add_fn_1": rolesDefault,
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault
  },
  "Models": {
    "add_fn_1": rolesDefault,
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault
  },
  "Product all": {
    "add_fn_1": rolesDefault
  },
  "Software": {
    "add_fn_1": rolesDefault,
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault,
    "msu_fn_1": rolesDefault,
    "msd_fn_1": rolesDefault,
  },
  "Dealer Device Assignments": {
    "add_fn_1": rolesBackOffice,
    "del_fn_1": rolesBackOffice,
    "msd_fn_1": rolesBackOffice,
  },
  "Shop Owner Device Assignments": {
    "add_fn_1": rolesDealer,
    "del_fn_1": rolesDealer,
    "msd_fn_1": rolesDealer,
  },
  "Documentation Management": {
    "add_fn_1": rolesDefault,
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault,
    "msu_fn_1": rolesDefault,
    "msd_fn_1": rolesDefault,
  },
  "Video Management": {
    "add_fn_1": rolesDefault,
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault
  },
  "ADocumentation Management": {
    "add_fn_1": rolesDefault,
    "del_fn_1": rolesDefault
  },
  "Histories": {
    "add_fn_1": rolesDefault,
    "del_fn_1": rolesDefault
  },
  "Preventive maintenance": {
    "add_fn_1": rolesMaintenance,
    "upd_fn_1": rolesMaintenance,
    "del_fn_1": rolesMaintenance,

    "add_fn_2": rolesMaintenance,
  },
  "Manage maintenance products": {
    "lnk_fn_1": rolesMaintenance
  },
  "Product-details": {
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault,
    "chg_fn_1": rolesDefault,
    
    // Maintenance Table
    "add_fn_2": rolesMaintenance,
    "del_fn_2": rolesMaintenance,

    // Maintenance Devices Table
    "add_fn_3": rolesMaintenance,
    "lnk_fn_3": rolesMaintenance,
    "upd_fn_3": rolesMaintenance,
  },
  "Device-details": {
    "opn_fn_1": rolesDefault,
    "upd_fn_1": rolesMaintenance,
    "dte_fn_1": rolesMaintenance,
    "chg_fn_1": rolesDefault,
    "del_fn_1": rolesDefault,
    "chg_fn_2": rolesDefault,
    "opn_fn_3": rolesDefault,
    "opn_fn_4": rolesDefault,
  },
  "Report-message": {
    "del_fn_1": rolesDefault,
  },
  "Product-firmware": {
    "add_fn_1": rolesDefault,
    "upd_fn_1": rolesDefault,
    "del_fn_1": rolesDefault,
  },
  "Document": {
    "add_fn_1": rolesDefault,
    "del_fn_1": rolesDefault,
  },
};

export function getPermissionsFunctions(
  route: any, 
  userData: any): string[] {
  const routeName = route?.name;
  const groupId = userData?.web_group_id;
  const routePermissions = permissionsMap[routeName];
  if (!routePermissions) return [];

  return Object.entries(routePermissions)
    .filter(([_, groupIds]) => groupIds.includes(groupId))
    .map(([fnName]) => fnName);
}

export function formatDuration(t: any, seconds: number) {
  const units = [
    { label: t('years'), seconds: 365 * 24 * 60 * 60 },
    { label: t('months'), seconds: 30 * 24 * 60 * 60 },
    { label: t('days'), seconds: 24 * 60 * 60 },
    { label: t('hours'), seconds: 60 * 60 },
    { label: t('minutes'), seconds: 60 },
    { label: t('seconds'), seconds: 1 }
  ];

  const result = [];

  for (const unit of units) {
    const value = Math.floor(seconds / unit.seconds);
    if (value > 0) {
      result.push(`${value}${unit.label}${value > 1 && unit.label !== 'minutes' && unit.label !== 'seconds' ? '' : ''}`);
      seconds %= unit.seconds;
    }
  }

  return result.join(' ');
}
